import { el } from "../dom.ts";
import { aperture } from "./aperture.ts";
import { icon } from "./icon.ts";

/**
 * Progress indicator for a multi-step flow.
 *
 * A real `<ol>` inside a labelled `<nav>`, with `aria-current="step"` on the
 * active item, so the sequence and the position in it are conveyed
 * structurally rather than by styling alone. Completed steps carry a check icon
 * and a visually hidden "completed"/"current" word, since a filled circle means
 * nothing to a screen reader.
 *
 * Below 30rem the labels are visually hidden and only the markers remain — the
 * UI UX Pro Max `compact-label-overflow` rule prefers dropping to an accessible
 * compact form over truncating text into ambiguity.
 */

export interface StepIndicatorOptions {
  readonly steps: readonly string[];
  /** Zero-based index of the current step. */
  readonly current: number;
  readonly label?: string;
}

export function stepIndicator(options: StepIndicatorOptions): HTMLElement {
  const items = options.steps.flatMap((step, index) => {
    const done = index < options.current;
    const current = index === options.current;
    const stateWord = done ? "completed" : current ? "current" : "not started";

    const item = el(
      "li",
      {
        class: "nura-steps__item",
        data: { state: done ? "done" : current ? "current" : "todo" },
        attrs: { "aria-current": current ? "step" : undefined },
      },
      [
        el("span", { class: "nura-steps__marker" }, [
          // The aperture states carry the progress: closed behind you, open on
          // the step you are standing in, and a plain ring ahead.
          el("span", {
            class: "nura-steps__ring",
            staticHtml: aperture(24, { state: done || !current ? "closed" : "open", weight: 7 }),
          }),
          el("span", {
            class: "nura-steps__glyph",
            text: done ? undefined : String(index + 1),
            staticHtml: done ? icon("check", 12) : undefined,
          }),
        ]),
        el("span", { class: "nura-steps__label", text: step }),
        el("span", { class: "nura-sr-only", text: `, ${stateWord}` }),
      ],
    );

    const rule =
      index < options.steps.length - 1
        ? el("li", { class: "nura-steps__rule", attrs: { "aria-hidden": true } })
        : undefined;

    return rule === undefined ? [item] : [item, rule];
  });

  return el("nav", { attrs: { "aria-label": options.label ?? "Progress" } }, [
    el("ol", { class: "nura-steps" }, items),
  ]);
}
